import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useLeadModal } from "../context/LeadModalContext";

const links = [
    { to: "/", label: "Home", end: true },
    { to: "/#services", label: "Services", hash: true },
    { to: "/#process", label: "Process", hash: true },
    { to: "/pricing", label: "Pricing" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { openModal } = useLeadModal();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname, location.hash]);

    const renderLink = (l) => {
        if (l.hash) {
            return (
                <a
                    key={l.label}
                    href={l.to}
                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                    className="text-sm text-[#94a3b8] hover:text-white transition-colors"
                >
                    {l.label}
                </a>
            );
        }
        return (
            <NavLink
                key={l.label}
                to={l.to}
                end={l.end}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                    `text-sm transition-colors ${
                        isActive ? "text-white" : "text-[#94a3b8] hover:text-white"
                    }`
                }
            >
                {l.label}
            </NavLink>
        );
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#050b14]/75 border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-16 sm:h-20 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center group">
                    <span className="font-heading font-extrabold text-2xl sm:text-[1.65rem] tracking-tight leading-none">
                        <span className="text-[#00f0ff]">
                            ez
                        </span>
                        <span className="text-white">buildabot</span>
                    </span>
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {links.map(renderLink)}
                </div>

                <div className="hidden md:flex items-center gap-3">
                    <button
                        onClick={() => openModal("nav")}
                        data-testid="nav-trial-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00f0ff] text-[#050b14] text-sm font-bold hover:bg-white transition-colors duration-300"
                    >
                        Book a Free Call
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>

                <button
                    onClick={() => setMobileOpen((v) => !v)}
                    data-testid="nav-mobile-toggle"
                    className="md:hidden w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-white hover:border-[#00f0ff]/40 transition-colors"
                    aria-label="Toggle menu"
                >
                    {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </nav>

            {mobileOpen && (
                <div
                    data-testid="nav-mobile-panel"
                    className="md:hidden absolute top-full inset-x-0 backdrop-blur-xl bg-[#050b14]/95 border-b border-white/10 px-6 py-6 flex flex-col gap-5"
                >
                    {links.map((l) =>
                        l.hash ? (
                            <a
                                key={l.label}
                                href={l.to}
                                onClick={() => setMobileOpen(false)}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                className="text-base text-[#cbd5e1] hover:text-[#00f0ff]"
                            >
                                {l.label}
                            </a>
                        ) : (
                            <NavLink
                                key={l.label}
                                to={l.to}
                                end={l.end}
                                onClick={() => setMobileOpen(false)}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-base ${isActive ? "text-[#00f0ff]" : "text-[#cbd5e1]"} hover:text-[#00f0ff]`
                                }
                            >
                                {l.label}
                            </NavLink>
                        )
                    )}
                    <button
                        onClick={() => {
                            setMobileOpen(false);
                            openModal("mobile-nav");
                        }}
                        data-testid="mobile-nav-trial-btn"
                        className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold mt-2"
                    >
                        Book a Free Call
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}
        </header>
    );
}
